/* Hallmark · component: DateRangeField · genre: modern-minimal · theme: maeiz-tokens
 * Custom dual calendar pickers — not native date inputs
 */
import React, { useState } from "react";
import { CalendarRange } from "lucide-react";
import { clsx } from "clsx";
import { FormField } from "../FormField";
import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Calendar } from "../Calendar";

function formatDisplay(iso?: string) {
    if (!iso) return "mm / dd / yyyy";
    const [y, m, d] = iso.split("-");
    return `${m} / ${d} / ${y}`;
}

export interface DateRangeFieldProps {
    id?: string;
    label: string;
    description?: string;
    error?: string;
    required?: boolean;
    startValue?: string;
    endValue?: string;
    onStartChange?: (value: string) => void;
    onEndChange?: (value: string) => void;
    className?: string;
    disabled?: boolean;
}

export const DateRangeField: React.FC<DateRangeFieldProps> = ({
    id,
    label,
    description,
    error,
    required,
    startValue,
    endValue,
    onStartChange,
    onEndChange,
    className,
    disabled,
}) => {
    const fieldId = id ?? `daterange-${label.replace(/\s+/g, "-").toLowerCase()}`;
    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);

    return (
        <FormField
            id={fieldId}
            label={label}
            description={description}
            error={error}
            required={required}
            className={className}
        >
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                <Popover open={startOpen} onOpenChange={setStartOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            id={`${fieldId}-start`}
                            type="button"
                            variant="outline"
                            disabled={disabled}
                            className={clsx("w-full justify-start font-normal tabular-nums", !startValue && "text-neutral-secondary")}
                            icon={CalendarRange}
                            aria-label={`${label} start`}
                        >
                            <span className="truncate">{formatDisplay(startValue)}</span>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-2">
                        <Calendar
                            value={startValue}
                            rangeStart={startValue}
                            rangeEnd={endValue}
                            max={endValue}
                            onChange={(v) => {
                                onStartChange?.(v);
                                setStartOpen(false);
                            }}
                        />
                    </PopoverContent>
                </Popover>

                <span className="hidden text-center text-xs font-medium uppercase tracking-wide text-neutral-secondary sm:block">
                    to
                </span>

                <Popover open={endOpen} onOpenChange={setEndOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            id={`${fieldId}-end`}
                            type="button"
                            variant="outline"
                            disabled={disabled}
                            className={clsx("w-full justify-start font-normal tabular-nums", !endValue && "text-neutral-secondary")}
                            icon={CalendarRange}
                            aria-label={`${label} end`}
                        >
                            <span className="truncate">{formatDisplay(endValue)}</span>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto p-2">
                        <Calendar
                            value={endValue}
                            rangeStart={startValue}
                            rangeEnd={endValue}
                            min={startValue}
                            onChange={(v) => {
                                onEndChange?.(v);
                                setEndOpen(false);
                            }}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        </FormField>
    );
};
